import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { ArrowRight, CheckCircle, Loader2 } from 'lucide-react'

const SUPABASE_URL = 'https://vvuhbraojktjsesoiljt.supabase.co/functions/v1'

const PLAN_LABELS: Record<string, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

export function SignupPage() {
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan') || 'starter'

  const [clubName, setClubName] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!clubName.trim() || !ownerName.trim() || !email.trim()) return

    setLoading(true)
    setError('')

    try {
      const res = await fetch(`${SUPABASE_URL}/auth-signup-club`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          club_name: clubName.trim(),
          owner_name: ownerName.trim(),
          email: email.trim().toLowerCase(),
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Registrierung fehlgeschlagen. Bitte versuche es erneut.')
        setLoading(false)
        return
      }

      setSuccess(true)
    } catch (err) {
      console.error(err)
      setError('Verbindungsfehler. Bitte versuche es erneut.')
    }
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-extrabold tracking-[0.25em]"
            style={{
              background: 'linear-gradient(135deg, #c8c8d0, #8a8a96, #c8c8d0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            VNVLD
          </h1>
          <p className="text-[#b0aeb8] mt-1">
            Club registrieren
            {PLAN_LABELS[plan] && (
              <span className="text-[#707078]"> — {PLAN_LABELS[plan]}</span>
            )}
          </p>
        </div>

        {success ? (
          <div className="bg-[#131316] border border-white/5 rounded-2xl p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Pr&uuml;fe deine E-Mail!</h2>
            <p className="text-[#b0aeb8] mb-4">
              Wir haben einen Magic Link an{' '}
              <span className="text-white">{email}</span> geschickt.
              Klicke auf den Link in der E-Mail, um dich anzumelden und das Onboarding zu starten.
            </p>
            <p className="text-sm text-[#707078]">
              Kein Link erhalten? Pr&uuml;fe deinen Spam-Ordner.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#131316] border border-white/5 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-white mb-6">Neuen Club anlegen</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label htmlFor="clubName" className="block text-sm text-[#b0aeb8] mb-1.5">
                  Club-Name
                </label>
                <input
                  id="clubName"
                  type="text"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  placeholder="z.B. Club Paradiso"
                  className="w-full px-4 py-3 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label htmlFor="ownerName" className="block text-sm text-[#b0aeb8] mb-1.5">
                  Betreiber-Name
                </label>
                <input
                  id="ownerName"
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Vor- und Nachname"
                  className="w-full px-4 py-3 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
                  required
                />
              </div>

              <div>
                <label htmlFor="signupEmail" className="block text-sm text-[#b0aeb8] mb-1.5">
                  E-Mail-Adresse
                </label>
                <input
                  id="signupEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  className="w-full px-4 py-3 bg-white/5 border border-white/5 rounded-xl text-white placeholder-[#707078] focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-4">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !clubName.trim() || !ownerName.trim() || !email.trim()}
              className="w-full py-3 bg-white/10 hover:bg-white/15 border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Club registrieren
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-xs text-[#707078] text-center mt-4">
              30 Tage kostenlos. Keine Kreditkarte n&ouml;tig.
            </p>
          </form>
        )}

        <p className="text-center text-sm text-[#707078] mt-6">
          Bereits registriert?{' '}
          <Link to="/login" className="text-[#c8c8d0] hover:underline">
            Anmelden
          </Link>
        </p>

        <p className="text-center text-xs text-[#707078]/50 mt-4">
          Powered by VNVLD — Dein Club, dein System.
        </p>
      </div>
    </div>
  )
}
